import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { cookieService } from '../services/cookieService';

export type Theme = 'dark' | 'light';

const THEME_COOKIE = 'theme';
const GALAXY_COOKIE = 'galaxyBackgroundEnabled';
const THEME_COOKIE_DAYS = 365;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** Whether the animated galaxy background is enabled (only affects dark theme, desktop). */
  galaxyEnabled: boolean;
  setGalaxyEnabled: (enabled: boolean) => void;
  toggleGalaxy: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Reads the persisted theme. The inline boot script in index.html sets the
 * `data-theme` attribute before first paint (anti-FOUC); here we reconcile the
 * React state with the persisted cookie value, defaulting to dark.
 */
function getInitialTheme(): Theme {
  const fromAttr = typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-theme')
    : null;
  if (fromAttr === 'light' || fromAttr === 'dark') return fromAttr;

  const stored = cookieService.getCookie(THEME_COOKIE);
  return stored === 'light' ? 'light' : 'dark';
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Keep the UA form controls (scrollbars, date pickers, autofill) in sync.
  root.style.colorScheme = theme;
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [galaxyEnabled, setGalaxyEnabledState] = useState<boolean>(
    () => cookieService.getCookie(GALAXY_COOKIE) !== 'false'
  );

  useEffect(() => {
    applyTheme(theme);
    cookieService.setCookie(THEME_COOKIE, theme, THEME_COOKIE_DAYS);
  }, [theme]);

  useEffect(() => {
    cookieService.setCookie(GALAXY_COOKIE, String(galaxyEnabled), THEME_COOKIE_DAYS);
  }, [galaxyEnabled]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setGalaxyEnabled = useCallback((enabled: boolean) => {
    setGalaxyEnabledState(enabled);
  }, []);

  const toggleGalaxy = useCallback(() => {
    setGalaxyEnabledState((prev) => !prev);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, galaxyEnabled, setGalaxyEnabled, toggleGalaxy }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
